import { useMemo, useRef, useState } from "react";
import Papa from "papaparse";
import { toast } from "sonner";
import { Check, FileSpreadsheet, Upload, X } from "lucide-react";
import { Button, Card, CardBody } from "@/design/components";
import { useUploadStatement } from "@/domain/useOnboarding";
import { useFileDrop } from "@/domain/useFileDrop";
import type { StatementImportResult, StatementRowInput } from "@/domain/api";

/**
 * Bank-statement CSV upload for the wizard's bank step (spec §9, method
 * "statement"). The file is parsed here in the browser — the server only ever
 * receives structured rows. Columns are auto-detected from the header row and
 * the statement is imported as-is; there is deliberately no manual column
 * mapping (a bank statement is a record of fact, not something the user edits).
 * If auto-detection can't find a date and an amount, the import is refused with
 * a clear message rather than asking the user to remap.
 */

export type StatementImportSummary = {
  fileName: string;
  imported: number;
  deduped: number;
  skipped: number;
};

// Header auto-detection, most-specific first. Lowercased substring match.
const DATE_HINTS = ["transaction date", "value date", "posting date", "date"];
const DESC_HINTS = ["description", "narrative", "particulars", "details", "remarks", "reference"];
const AMOUNT_HINTS = ["transaction amount", "amount"];
const DEBIT_HINTS = ["debit", "withdrawal", "money out", "paid out", "dr"];
const CREDIT_HINTS = ["credit", "deposit", "money in", "paid in", "cr"];
const CURRENCY_HINTS = ["currency", "ccy", "curr"];

// Statement market default. A currency column in the file overrides per-row.
const DEFAULT_CURRENCY = "UGX";

const NONE = "__none__";

function detect(headers: string[], hints: string[]): string {
  for (const hint of hints) {
    const hit = headers.find((h) => h.toLowerCase().includes(hint));
    if (hit) return hit;
  }
  return NONE;
}

// Statement dates in this market are almost always day-first (13/06/2026,
// 13-06-26); ISO (2026-06-13) passes through. Returns null when unparseable.
function parseStatementDate(raw: string): Date | null {
  const s = raw.trim();
  if (!s) return null;
  const dayFirst = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (dayFirst) {
    const [, d, m, y] = dayFirst;
    const year = y.length === 2 ? 2000 + Number(y) : Number(y);
    const date = new Date(Date.UTC(year, Number(m) - 1, Number(d)));
    return Number.isNaN(date.getTime()) || Number(m) > 12 ? null : date;
  }
  const fallback = new Date(s);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

// "1,250,000.00" / "(3 500)" / "-3500" → signed number, null when not numeric.
function parseAmount(raw: string): number | null {
  const s = raw.trim();
  if (!s) return null;
  const negative = /^\(.*\)$/.test(s) || s.startsWith("-");
  const cleaned = s.replace(/[()\s,]/g, "").replace(/^-/, "");
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n === 0) return null;
  return negative ? -n : n;
}

async function sha256Hex(file: File): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// The columns auto-detected from the header row. `ok` is the minimum needed to
// import: a date, plus either a signed amount column or a debit/credit pair.
type Cols = { date: string; description: string; amount: string; debit: string; credit: string; currency: string };
type Parsed = { fileName: string; sha256: string; records: Record<string, string>[]; cols: Cols; ok: boolean };

export function StatementUpload({
  sessionId,
  onImported,
}: {
  sessionId: number;
  onImported: (summary: StatementImportSummary) => void;
}) {
  const { uploadStatement, isPending, error } = useUploadStatement();
  const fileInput = useRef<HTMLInputElement>(null);
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [showAllRows, setShowAllRows] = useState(false);

  const rows: StatementRowInput[] = useMemo(() => {
    if (!parsed || !parsed.ok) return [];
    const { cols } = parsed;
    const usesDebitCredit = cols.amount === NONE;
    const seen = new Map<string, number>();
    const out: StatementRowInput[] = [];
    for (const rec of parsed.records) {
      const date = parseStatementDate(rec[cols.date] ?? "");
      if (!date) continue;

      let amount: number | null = null;
      let direction: "inflow" | "outflow" | null = null;
      if (usesDebitCredit) {
        const debit = cols.debit !== NONE ? parseAmount(rec[cols.debit] ?? "") : null;
        const credit = cols.credit !== NONE ? parseAmount(rec[cols.credit] ?? "") : null;
        if (debit != null) {
          amount = Math.abs(debit);
          direction = "outflow";
        } else if (credit != null) {
          amount = Math.abs(credit);
          direction = "inflow";
        }
      } else {
        const signed = parseAmount(rec[cols.amount] ?? "");
        if (signed != null) {
          amount = Math.abs(signed);
          direction = signed < 0 ? "outflow" : "inflow";
        }
      }
      if (amount == null || !direction) continue;

      const currency =
        cols.currency !== NONE && (rec[cols.currency] ?? "").trim()
          ? rec[cols.currency].trim().toUpperCase().slice(0, 10)
          : DEFAULT_CURRENCY;
      const description = cols.description !== NONE ? (rec[cols.description] ?? "").trim() : "";
      // Content-keyed reference, sha-prefixed so dedup is per-file-content and
      // re-uploading the same export is a no-op server-side. The occurrence
      // counter keeps genuinely identical lines within one file distinct while
      // staying stable across re-uploads (unlike a raw row index).
      const key = `${date.toISOString().slice(0, 10)}|${direction}|${amount}|${description.slice(0, 60)}`;
      const nth = (seen.get(key) ?? 0) + 1;
      seen.set(key, nth);
      out.push({
        importReference: `${parsed.sha256.slice(0, 16)}:${key}#${nth}`,
        direction,
        amount,
        currency,
        occurredAt: date.toISOString(),
        description: description || undefined,
      });
    }
    return out;
  }, [parsed]);

  // Single-file, matching this drop zone's existing e.dataTransfer.files[0]
  // semantics (a bank statement is one file, not a batch).
  const fileDrop = useFileDrop({ onFiles: (files) => { if (files[0]) void handleFile(files[0]); } });

  async function handleFile(file: File) {
    if (!/\.csv$/i.test(file.name)) {
      toast.error("Only .csv files are supported. Export your statement as CSV from your bank's portal.");
      return;
    }
    const sha256 = await sha256Hex(file);
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: "greedy",
      complete: (res) => {
        const headers = (res.meta.fields ?? []).filter(Boolean);
        if (!headers.length || !res.data.length) {
          toast.error("That file has no readable rows. Check it's a transaction export, not a summary.");
          return;
        }
        const amount = detect(headers, AMOUNT_HINTS);
        const cols: Cols = {
          date: detect(headers, DATE_HINTS),
          description: detect(headers, DESC_HINTS),
          amount,
          // Only reach for debit/credit columns when no single amount column exists.
          debit: amount === NONE ? detect(headers, DEBIT_HINTS) : NONE,
          credit: amount === NONE ? detect(headers, CREDIT_HINTS) : NONE,
          currency: detect(headers, CURRENCY_HINTS),
        };
        const ok = cols.date !== NONE && (cols.amount !== NONE || cols.debit !== NONE || cols.credit !== NONE);
        setParsed({ fileName: file.name, sha256, records: res.data, cols, ok });
        setShowAllRows(false);
      },
      error: () => toast.error("We couldn't read that file. Re-export it as CSV and try again."),
    });
  }

  async function handleImport() {
    if (!parsed || rows.length === 0) return;
    const result: StatementImportResult | null = await uploadStatement(sessionId, rows, {
      name: parsed.fileName,
      sha256: parsed.sha256,
    });
    if (!result) return;
    toast.success(
      result.imported > 0
        ? `${result.imported} transaction${result.imported === 1 ? "" : "s"} imported.`
        : "Statement already imported — no new transactions.",
    );
    onImported({ fileName: parsed.fileName, imported: result.imported, deduped: result.deduped, skipped: result.skipped });
  }

  // Stale/mismatched onboarding session surfaces as a 403 from the ownership
  // check — translate it into something the user can act on.
  const displayError =
    error === "not_your_session" || error === "session_not_found" || error === "session_has_no_organisation"
      ? "This device isn't signed in to the onboarding session. Reopen your invite link and try again."
      : error;

  if (!parsed) {
    return (
      <button
        type="button"
        onClick={() => fileInput.current?.click()}
        onDragOver={fileDrop.onDragOver}
        onDragLeave={fileDrop.onDragLeave}
        onDrop={fileDrop.onDrop}
        className={`mt-4 flex w-full flex-col items-center gap-2 rounded-lg border border-dashed p-8 text-center transition-colors ${fileDrop.isDraggingOver ? "border-brand bg-brandSoft" : "border-strong bg-card hover:border-brand"}`}
      >
        <Upload className="h-6 w-6 text-brand" aria-hidden />
        <p className="text-sm font-medium text-ink">Drop your bank statement here, or click to choose</p>
        <p className="text-xs text-mute">CSV export from your bank's online portal. The file stays on your device — only the transaction lines are sent.</p>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = "";
          }}
        />
      </button>
    );
  }

  return (
    <Card className="mt-4">
      <CardBody className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 shrink-0 text-brand" aria-hidden />
            <p className="truncate text-sm font-medium text-ink">{parsed.fileName}</p>
            <p className="shrink-0 text-xs text-mute">{parsed.records.length} rows</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setParsed(null)} disabled={isPending}>
            <X className="h-4 w-4" aria-hidden /> Remove
          </Button>
        </div>

        {!parsed.ok ? (
          <p className="text-sm text-bad">
            We couldn't read the columns in this statement automatically — it needs a date and an amount column.
            Export a standard transaction statement (CSV) from your bank and try again.
          </p>
        ) : rows.length > 0 ? (
          <>
            <p className="flex items-center gap-1.5 text-xs text-mute">
              <Check className="h-3.5 w-3.5 text-ok" aria-hidden />
              {rows.length} transaction{rows.length === 1 ? "" : "s"} read. Amounts in {rows[0].currency}.
            </p>
            <div className="overflow-hidden rounded-lg border border-line">
              <table className="w-full text-xs">
                <thead className="bg-subtle text-left text-faint">
                  <tr>
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Description</th>
                    <th className="px-3 py-2 text-right font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllRows ? rows : rows.slice(0, 6)).map((r) => (
                    <tr key={r.importReference} className="border-t border-line">
                      <td className="px-3 py-1.5 tabular-nums text-mute">{r.occurredAt.slice(0, 10)}</td>
                      <td className="max-w-0 truncate px-3 py-1.5 text-ink">{r.description ?? "—"}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums ${r.direction === "outflow" ? "text-bad" : "text-ok"}`}>
                        {r.direction === "outflow" ? "−" : "+"}{r.amount.toLocaleString()} {r.currency}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 6 && (
                <button
                  type="button"
                  onClick={() => setShowAllRows((v) => !v)}
                  className="w-full border-t border-line bg-subtle px-3 py-1.5 text-left text-xs font-medium text-brand hover:underline"
                  aria-expanded={showAllRows}
                >
                  {showAllRows ? "Show fewer" : `…and ${rows.length - 6} more`}
                </button>
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-mute">No transactions with a readable date and amount were found in this file.</p>
        )}

        {displayError && <p className="text-sm text-bad">{displayError}</p>}

        <div className="flex justify-end">
          <Button onClick={() => void handleImport()} loading={isPending} disabled={rows.length === 0}>
            Import {rows.length > 0 ? `${rows.length} transaction${rows.length === 1 ? "" : "s"}` : "transactions"}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
